import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, Copy, Loader2, Download, RefreshCw, AlertTriangle } from 'lucide-react';

// Encodes otpauth URI into a QR code via public QR service (no extra dep needed)
const qrSrc = (uri) => `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(uri)}`;

const RecoveryCodesDisplay = ({ codes, onClose }) => {
  const download = () => {
    const content = `Paydaai SuperAdmin - Yedek Kodlar\nOluşturulma: ${new Date().toLocaleString('tr-TR')}\n\n${codes.join('\n')}\n\n⚠ Her kod yalnızca 1 kez kullanılabilir.\n⚠ Bu kodları güvenli bir yerde saklayın.\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `paydaai-recovery-codes-${Date.now()}.txt`;
    a.click();
    toast.success('İndirildi');
  };
  const copy = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    toast.success('Kopyalandı');
  };
  return (
    <div className="space-y-3" data-testid="recovery-codes-display">
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800">
          <p className="font-semibold mb-1">Bu kodları şimdi kaydedin!</p>
          <p>Her kod <strong>1 kez</strong> kullanılabilir. Telefonunuzu kaybederseniz bu kodlarla giriş yapıp 2FA'yı sıfırlayabilirsiniz. Bu ekran kapandıktan sonra kodları <strong>tekrar göremezsiniz</strong>.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 bg-[#F9F8F6] border border-[#E5E5E5] rounded-lg p-3 font-mono text-sm" data-testid="recovery-codes-grid">
        {codes.map((c, i) => (
          <code key={i} className="text-[#1A1A2E] tracking-wide" data-testid={`recovery-code-${i}`}>
            {String(i + 1).padStart(2, '0')}. {c}
          </code>
        ))}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={copy} className="flex-1" data-testid="copy-recovery-codes">
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Kopyala
        </Button>
        <Button type="button" variant="outline" onClick={download} className="flex-1" data-testid="download-recovery-codes">
          <Download className="w-3.5 h-3.5 mr-1.5" /> İndir (.txt)
        </Button>
      </div>
      <Button type="button" onClick={onClose} className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="close-recovery-codes">
        Kodları Kaydettim
      </Button>
    </div>
  );
};

export const TwoFactorSection = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('view'); // view | setup | disable | regen | codes

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/superadmin/2fa/status`);
      setStatus(data);
    } catch { /* empty */ }
  }, [API_URL]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/superadmin/2fa/setup`, {});
      setSetupData(data);
      setMode('setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || '2FA kurulumu başlatılamadı');
    }
    setLoading(false);
  };

  const confirmEnable = async () => {
    if (verifyCode.length !== 6) { toast.error('6 haneli kod girin'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/superadmin/2fa/verify`, { totp_code: verifyCode });
      toast.success('2FA etkinleştirildi');
      setRecoveryCodes(data.recovery_codes || []);
      setMode('codes');
      setSetupData(null);
      setVerifyCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Doğrulama başarısız');
    }
    setLoading(false);
  };

  const confirmDisable = async () => {
    if (disableCode.length !== 6) { toast.error('6 haneli kod girin'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/superadmin/2fa/disable`, { totp_code: disableCode });
      toast.success('2FA devre dışı bırakıldı');
      setMode('view');
      setDisableCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
    setLoading(false);
  };

  const confirmRegen = async () => {
    if (regenCode.length !== 6) { toast.error('6 haneli kod girin'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/superadmin/2fa/regenerate-recovery`, { totp_code: regenCode });
      toast.success('Yedek kodlar yenilendi');
      setRecoveryCodes(data.recovery_codes || []);
      setMode('codes');
      setRegenCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
    setLoading(false);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData.secret);
    toast.success('Gizli anahtar kopyalandı');
  };

  if (!status) {
    return <div className="text-sm text-[#595959]">Yükleniyor...</div>;
  }

  if (mode === 'codes' && recoveryCodes) {
    return <RecoveryCodesDisplay codes={recoveryCodes} onClose={() => { setRecoveryCodes(null); setMode('view'); }} />;
  }

  if (mode === 'setup' && setupData) {
    return (
      <div className="space-y-3" data-testid="2fa-setup-section">
        <p className="text-xs text-[#595959]">
          1. Google Authenticator uygulamasında <strong>+</strong> simgesine dokunun<br />
          2. Aşağıdaki QR kodunu okutun<br />
          3. Uygulamada görünen 6 haneli kodu girin
        </p>
        <div className="flex justify-center bg-white p-3 rounded-lg border border-[#E5E5E5]">
          <img src={qrSrc(setupData.otpauth_uri)} alt="2FA QR Code" className="w-44 h-44" data-testid="2fa-qr-image" />
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-[#F9F8F6] border border-[#E5E5E5] rounded px-2 py-1.5 font-mono text-[#1A1A2E] break-all" data-testid="2fa-secret">
            {setupData.secret}
          </code>
          <Button type="button" variant="outline" size="sm" onClick={copySecret} data-testid="copy-2fa-secret">
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div>
          <Label className="text-xs">Doğrulama Kodu</Label>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center text-lg tracking-[0.3em] font-mono"
            data-testid="2fa-verify-code"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => { setMode('view'); setSetupData(null); setVerifyCode(''); }} data-testid="cancel-2fa-setup">
            İptal
          </Button>
          <Button type="button" onClick={confirmEnable} disabled={loading} className="flex-1 bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="confirm-enable-2fa">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Etkinleştir'}
          </Button>
        </div>
      </div>
    );
  }

  if (mode === 'disable') {
    return (
      <div className="space-y-3" data-testid="2fa-disable-section">
        <p className="text-xs text-amber-700">
          2FA'yı devre dışı bırakmak için mevcut kodu girin.
        </p>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={disableCode}
          onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center text-lg tracking-[0.3em] font-mono"
          data-testid="2fa-disable-code"
        />
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => { setMode('view'); setDisableCode(''); }}>
            İptal
          </Button>
          <Button type="button" onClick={confirmDisable} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-disable-2fa">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Devre Dışı Bırak'}
          </Button>
        </div>
      </div>
    );
  }

  if (mode === 'regen') {
    return (
      <div className="space-y-3" data-testid="2fa-regen-section">
        <p className="text-xs text-amber-700">
          Eski yedek kodlar geçersiz olacak. Yeni kodları almak için mevcut 2FA kodunu girin.
        </p>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={regenCode}
          onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center text-lg tracking-[0.3em] font-mono"
          data-testid="2fa-regen-code"
        />
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => { setMode('view'); setRegenCode(''); }}>
            İptal
          </Button>
          <Button type="button" onClick={confirmRegen} disabled={loading} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-regen-recovery">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yeniden Oluştur'}
          </Button>
        </div>
      </div>
    );
  }

  // view mode
  return (
    <div className="space-y-2" data-testid="2fa-view-section">
      {status.enabled ? (
        <>
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">2FA Etkin</p>
              <p className="text-xs text-emerald-600">
                Yedek kod: <strong>{status.recovery_codes_remaining || 0}</strong> adet kaldı
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setMode('regen')} data-testid="open-regen-recovery">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yedek Kodları Yenile
            </Button>
            <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setMode('disable')} data-testid="open-disable-2fa">
              Devre Dışı Bırak
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <ShieldAlert className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">2FA Kapalı</p>
              <p className="text-xs text-amber-600">Güvenlik için etkinleştirmenizi öneririz.</p>
            </div>
          </div>
          <Button type="button" onClick={startSetup} disabled={loading} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white" data-testid="enable-2fa-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Google Authenticator ile Etkinleştir'}
          </Button>
        </>
      )}
    </div>
  );
};
